import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, RotateCw } from 'lucide-react';
import AuthTurnstile from '@/components/auth/AuthTurnstile.jsx';
import { useTranslation } from '@/components/i18n/TranslationProvider';
import { buttonVariants } from '@/components/ui/button';
import { requestAuthEmailConfirmationResend } from '@/lib/authEmailConfirmationResend.js';
import { normalizeAuthEmail } from '@/lib/authEmailOtp.js';
import { getAuthTurnstileConfiguration } from '@/lib/authTurnstile.js';
import { cn } from '@/lib/utils';

export const EMAIL_CONFIRMATION_RESEND_COOLDOWN_MS = 60_000;

export default function EmailConfirmationResend({
  email,
  disabled = false,
  idPrefix = 'email-confirmation-resend',
  className,
}) {
  const { t } = useTranslation();
  const cooldownTimerRef = useRef(null);
  const [resendState, setResendState] = useState('idle');
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaResetKey, setCaptchaResetKey] = useState(0);
  const turnstileConfiguration = getAuthTurnstileConfiguration();
  const isCaptchaBlocking = turnstileConfiguration.enabled && !captchaToken;
  const isCoolingDown = resendState === 'success';
  const isPending = resendState === 'pending';

  useEffect(() => () => {
    if (cooldownTimerRef.current) {
      window.clearTimeout(cooldownTimerRef.current);
    }
  }, []);

  const resetCaptcha = useCallback(() => {
    if (!turnstileConfiguration.enabled) return;
    setCaptchaToken(null);
    setCaptchaResetKey((current) => current + 1);
  }, [turnstileConfiguration.enabled]);

  const beginCooldown = useCallback(() => {
    if (cooldownTimerRef.current) {
      window.clearTimeout(cooldownTimerRef.current);
    }
    cooldownTimerRef.current = window.setTimeout(() => {
      setResendState('idle');
      cooldownTimerRef.current = null;
    }, EMAIL_CONFIRMATION_RESEND_COOLDOWN_MS);
  }, []);

  const handleResend = useCallback(async () => {
    if (disabled || isPending || isCoolingDown || isCaptchaBlocking) return;

    const normalizedEmail = normalizeAuthEmail(email);
    if (!normalizedEmail) {
      setResendState('invalid-email');
      return;
    }

    setResendState('pending');
    try {
      await requestAuthEmailConfirmationResend({
        email: normalizedEmail,
        ...(captchaToken ? { captchaToken } : {}),
      });

      setResendState('success');
      beginCooldown();
    } catch {
      setResendState('error');
    } finally {
      resetCaptcha();
    }
  }, [
    beginCooldown,
    captchaToken,
    disabled,
    email,
    isCaptchaBlocking,
    isCoolingDown,
    isPending,
    resetCaptcha,
  ]);

  const feedbackId = `${idPrefix}-feedback`;
  const feedback = resendState === 'success'
    ? { role: 'status', text: t('auth.email_confirmation_resend_success') }
    : resendState === 'invalid-email'
      ? { role: 'alert', text: t('auth.email_otp_invalid_email') }
      : resendState === 'error'
        ? { role: 'alert', text: t('auth.email_confirmation_resend_error') }
        : null;

  return (
    <div className={cn('space-y-2', className)} data-testid={`${idPrefix}-control`}>
      <AuthTurnstile
        action="auth_signup_resend"
        configuration={turnstileConfiguration}
        onTokenChange={setCaptchaToken}
        resetKey={captchaResetKey}
      />
      <button
        type="button"
        onClick={handleResend}
        disabled={disabled || isPending || isCoolingDown || isCaptchaBlocking}
        aria-describedby={feedback ? feedbackId : undefined}
        className={cn(
          buttonVariants({ variant: 'outline', size: 'sm' }),
          'auth-focus-visible min-h-11 w-full border-white/25 text-white hover:bg-white/10 disabled:text-white/45',
        )}
      >
        {isPending ? (
          <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        ) : (
          <RotateCw className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
        )}
        {isPending
          ? t('auth.email_confirmation_resending')
          : t('auth.email_confirmation_resend')}
      </button>
      {feedback ? (
        <p
          id={feedbackId}
          className={feedback.role === 'status' ? 'text-xs text-emerald-200' : 'text-xs text-red-200'}
          role={feedback.role}
        >
          {feedback.text}
        </p>
      ) : null}
    </div>
  );
}
